import Network from 'lib/api/network';
import { set } from 'lodash';

export default {
  namespaced: true,

  state() {
    return {
      config: null,
      dollarsPerPoint: null,
    };
  },

  mutations: {
    updateConfigKey(state, { key, keys, value }) {
      const effectiveKeys = keys || [key];
      set(state.config, effectiveKeys, value);
    },
  },

  getters: {
    emojiMultiplier: () => ({ points, emoji }) => (!points || points > 5 ? `:${emoji}:` : Array(points).fill(`:${emoji}:`).join('')),

    pointsPhrase: (_state, getters) => ({ points, emoji, pointsName }) => {
      const pointsStr = points ?? '';
      const emojiStr = emoji ? getters.emojiMultiplier({ points, emoji }) : '';
      const nameStr = pointsName ?? '';
      return [pointsStr, nameStr, emojiStr].filter(Boolean).join(' ');
    },

    pointsPhraseFromConfig: (state, getters) => ({
      points,
      config,
      hideEmoji,
      hideName,
      hideNumber,
    }) => {
      const effectiveConfig = config || state.config;
      const emoji = hideEmoji ? null : effectiveConfig.emoji;
      let pointsName;
      if (!hideName) pointsName = points === 1 ? effectiveConfig.points_name : effectiveConfig.points_name_plural;
      // hideNumber picks the singular/plural name off `points` without printing it, like no_num: in num_points_text.
      return getters.pointsPhrase({ points: hideNumber ? null : points, emoji, pointsName });
    },

    exchangeRateText(state) {
      return `Exchange rate 1 ${state.config.points_name} = ${Math.round(state.dollarsPerPoint * 100)}¢ `
        + `:${state.config.emoji}: applies if monetary rewards are set up.`;
    },
  },

  actions: {
    updatePointsConfig({ commit, dispatch }, { key, val }) {
      commit('updateConfigKey', { keys: [key], value: val });
      return new Promise((resolve, reject) => {
        const params = { points_config: { [key]: val } };
        Network.put('/points/config', params, {
          success: () => { resolve(); },
          error: () => {
            reject();
            dispatch('setErrorToast', null, { root: true });
            commit('updateConfigKey', { keys: [key], value: !val });
          },
        });
      });
    },

    toggleCoffeeConfiguration({ dispatch }, { coffeeConfiguration, val }) {
      return new Promise((resolve, reject) => {
        dispatch('updateCoffeeConfiguration', { coffeeConfiguration, points: val ? 1 : 0 })
          .then(() => resolve())
          .catch(() => reject());
      });
    },

    updateCoffeeConfiguration({ state, commit, dispatch }, { coffeeConfiguration, points }) {
      const oldPoints = coffeeConfiguration.points_on_completion;
      const configIdx = state.config.coffee_configurations.findIndex(cc => cc.id === coffeeConfiguration.id);
      commit('updateConfigKey', { keys: ['coffee_configurations', configIdx, 'points_on_completion'], value: points });
      return new Promise((resolve, reject) => {
        const params = {
          points_config: { award_points_for_intros: true },
          coffee_configurations_attributes: { [coffeeConfiguration.id]: { points_on_completion: points } },
        };
        Network.put('/points/config', params, {
          success: () => { resolve(); },
          error: () => {
            commit('updateConfigKey', { keys: ['coffee_configurations', configIdx, 'points_on_completion'], value: oldPoints });
            dispatch('setErrorToast', null, { root: true });
            reject();
          },
        });
      });
    },

    toggleBadgeConfig({ dispatch }, { badgeConfig, val }) {
      return new Promise((resolve, reject) => {
        dispatch('updateBadgeConfig', { badgeConfig, params: { active: val } })
          .then(() => resolve())
          .catch(() => reject());
      });
    },

    updateBadgeConfig({ state, commit, dispatch }, { badgeConfig, params }) {
      const configIdx = state.config.badge_configs.findIndex(bc => bc.badge.id === badgeConfig.badge.id);
      const oldConfig = { ...badgeConfig };
      Object.keys(params).forEach((key) => {
        commit('updateConfigKey', { keys: ['badge_configs', configIdx, key], value: params[key] });
      });
      return new Promise((resolve, reject) => {
        const fullParams = {
          points_config: { award_points_for_badges: true },
          badge_configs_attributes: { badge_id: badgeConfig.badge.id, ...params },
        };
        Network.put('/points/config', fullParams, {
          success: () => {
            resolve();
          },
          error: () => {
            commit('updateConfigKey', { keys: ['badge_configs', configIdx], value: oldConfig });
            dispatch('setErrorToast', null, { root: true });
            reject();
          },
        });
      });
    },
  },
};
